<template>
  <div>
    <vx-card title="QC Documentation">
      <div slot="no-body" class="mt-4">
        <vs-table :data="rawMaterials" stripe class="table-dark-inverted table-auto vs-con-loading__container">
          <template slot="thead">
            <vs-th width="22%">ITS PRODUCT CODE/NAME</vs-th>
            <vs-th width="22%">SUPPLIER PRODUCT CODE/NAME</vs-th>
            <vs-th width="18%">APPROVAL STATUS</vs-th>
            <vs-th width="18%">PRODUCTION STAGE</vs-th>
            <vs-th width="20%">ECD DOWNLOAD</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                {{ data[indextr].company_product_code ? data[indextr].category.prefix + ' - ' + data[indextr].company_product_code : '-' }}
                <br>
                {{ data[indextr].company_product_name || '-' }}
              </vs-td>

              <vs-td>
                {{ data[indextr].supplier_product_code }}
                <br>
                {{ data[indextr].supplier_product_name }}
              </vs-td>

              <vs-td>
                <vs-chip transparent :color="statusColorFactory(data[indextr].status ? data[indextr].status.id : '')">
                  {{ data[indextr].status ? data[indextr].status.name : '' }}
                </vs-chip>
              </vs-td>

              <vs-td>
                <vs-chip transparent :color="data[indextr].raw_material_stage === 'production' ? 'primary' : 'warning'">
                  {{ data[indextr].raw_material_stage }}
                </vs-chip>
              </vs-td>

              <vs-td>
                <vs-button
                  size="small"
                  color="primary"
                  type="border"
                  icon-pack="feather"
                  icon="icon-download"
                  @click.stop="openPopup(data[indextr])">
                  ECD PDF
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
        <vs-pagination :total="meta.last_page || 0" v-model="page" class="p-5"></vs-pagination>
      </div>
    </vx-card>

    <!-- ECD Download Popup -->
    <vs-popup title="Download ECD PDF" :active.sync="popupOpen" :buttons-hidden="true">
      <div v-if="loadingFormulations" class="flex justify-center py-8">
        <span class="text-grey">Loading formulations...</span>
      </div>

      <div v-else class="p-4">
        <div class="mb-4">
          <p class="text-sm text-grey mb-1">Raw Material</p>
          <p class="font-semibold">{{ selectedRM ? selectedRM.supplier_product_code : '' }} — {{ selectedRM ? selectedRM.supplier_product_name : '' }}</p>
        </div>

        <div class="mb-6">
          <p class="text-sm text-grey mb-2">Select Formulation</p>
          <div v-if="formulations.length === 0" class="text-warning text-sm">No formulations linked to this raw material.</div>
          <v-select
            v-else
            v-model="selectedFormulation"
            :options="formulations"
            label="formulation_code"
            :reduce="f => f.id"
            placeholder="Select a formulation...">
            <template #option="{ formulation_code, name }">
              <span class="font-semibold">{{ formulation_code }}</span>
              <span class="ml-2 text-grey text-sm">{{ name }}</span>
            </template>
          </v-select>
        </div>

        <div class="mb-6">
          <p class="text-sm text-grey mb-2">Supplier Users</p>
          <v-select
            v-model="selectedUsers"
            :options="supplierUsers"
            :reduce="u => u.id"
            :getOptionLabel="u => (u.first_name + ' ' + u.last_name).trim()"
            placeholder="Select user...">
          </v-select>
        </div>

        <div class="flex justify-end" style="gap: 8px;">
          <vs-button type="border" color="dark" @click="popupOpen = false">Cancel</vs-button>
          <vs-button
            color="primary"
            icon-pack="feather"
            icon="icon-download"
            :disabled="!selectedFormulation || downloading"
            @click="downloadECD">
            Download
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const SupplierRawMaterialRepository = RepositoryFactory.get('supplierRawMaterial')
const SupplierRepository = RepositoryFactory.get('supplier')

export default {
  name: 'SupplierQCDocumentationView',
  components: { 'v-select': vSelect },
  data() {
    return {
      rawMaterials: [],
      meta: {},
      page: 1,
      supplierId: null,
      popupOpen: false,
      selectedRM: null,
      formulations: [],
      selectedFormulation: null,
      loadingFormulations: false,
      downloading: false,
      supplierUsers: [],
      selectedUsers: [],
    }
  },
  async mounted() {
    this.supplierId = this.$route.params.id
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.$vs.loading()
      try {
        const { data } = await SupplierRawMaterialRepository.getRawMaterialBySupplierId(this.supplierId, this.page, '', 10, '')
        this.rawMaterials = data.data
        this.meta = data.meta
      } finally {
        this.$vs.loading.close()
      }
    },
    async openPopup(rm) {
      this.selectedRM = rm
      this.formulations = []
      this.selectedFormulation = null
      this.supplierUsers = []
      this.selectedUsers = []
      this.popupOpen = true
      this.loadingFormulations = true
      try {
        const [formulationsRes, usersRes] = await Promise.all([
          SupplierRawMaterialRepository.getFormulationsAttachedToRawMaterial(rm.id, 1, null),
          SupplierRepository.getSupplierUsers(this.supplierId)
        ])
        this.formulations = formulationsRes.data.data
        this.supplierUsers = usersRes.data.data
      } finally {
        this.loadingFormulations = false
      }
    },
    async downloadECD() {
      this.downloading = true
      try {
        const response = await SupplierRepository.downloadECD(this.supplierId, this.selectedRM.id, this.selectedFormulation, this.selectedUsers || undefined)
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `ECD_${this.selectedRM.supplier_product_code}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.popupOpen = false
      } catch (e) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to download ECD PDF', position: 'top-right' })
      } finally {
        this.downloading = false
      }
    },
    statusColorFactory(id) {
      switch (id) {
        case 1: return 'warning'
        case 2: return 'danger'
        case 3: return 'success'
        default: return 'danger'
      }
    },
  },
  watch: {
    page() {
      this.loadData()
    },
  },
}
</script>
